import {Controller} from "@hotwired/stimulus"

const debounce = (func, wait) => {
    let timeout
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout)
            func.apply(this, args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
    }
}

export default class extends Controller {
    static targets = [
        "table", "rulesContainer", "sqlOutput", "orderField", "orderDirection",
        "limitValue", "noRulesHint", "distinct", "fieldsContainer", "joinsContainer",
        "groupByContainer", "templateSelect"
    ]
    static values = {
        initialSql: String,
        schema: Object
    }

    connect() {
        this.ruleIndex = 0
        this.fieldIndex = 0
        this.joinIndex = 0
        this.groupByIndex = 0
        this.parsing = false
        this.tables = this.schemaValue || {}
        this.renderTableOptions()
        this.generateSqlDebounced = debounce(this.generateSql.bind(this), 300)

        setTimeout(() => {
            this.loadInitialSql()
        }, 100)
    }

    loadInitialSql() {
        const currentVal = this.hasSqlOutputTarget ? this.sqlOutputTarget.value : ""
        const sql = (currentVal && currentVal.trim()) ? currentVal : this.initialSqlValue

        if (sql && sql.trim()) {
            this.sqlOutputTarget.value = sql
            this.parseSqlToForm()
        }
    }

    renderTableOptions() {
        if (!this.hasTableTarget) return
        let html = '<option value="">Selecione uma tabela...</option>'
        Object.entries(this.tables).forEach(([key, t]) => {
            const icon = this.getTableIcon(key)
            html += `<option value="${key}">${icon} ${t.label}</option>`
        })
        this.tableTarget.innerHTML = html
    }

    getTableIcon(key) {
        const icons = {
            pessoas: "👥", adesoes: "📝", planos: "📋", adesao_parcelas: "💰",
            atendimentos: "📞", campanhas: "📢", boletos: "🧾", beneficios: "🎁",
            adesao_dependentes: "👨‍👩‍👧", adesao_historicos: "📚", adesao_produtos: "📦"
        }
        return icons[key] || "📊"
    }

    getAggFunctions() {
        return [
            {value: "", label: "Nenhuma"},
            {value: "COUNT", label: "Contar"},
            {value: "SUM", label: "Somar"},
            {value: "AVG", label: "Média"},
            {value: "MIN", label: "Mínimo"},
            {value: "MAX", label: "Máximo"},
            {value: "COUNT_DISTINCT", label: "Contar únicos"}
        ]
    }

    getJoinTypes() {
        return [
            {value: "LEFT JOIN", label: "Incluir todos + relacionados"},
            {value: "INNER JOIN", label: "Somente com relacionados"},
            {value: "RIGHT JOIN", label: "Todos da outra tabela"}
        ]
    }

    getOperators(fieldType) {
        const base = [
            {value: "=", label: "é igual a"},
            {value: "!=", label: "é diferente de"},
            {value: "IS NULL", label: "está vazio"},
            {value: "IS NOT NULL", label: "não está vazio"}
        ]
        const textOps = [
            {value: "LIKE", label: "contém"},
            {value: "NOT LIKE", label: "não contém"},
            {value: "STARTS", label: "começa com"},
            {value: "ENDS", label: "termina com"},
            {value: "IN", label: "está na lista"}
        ]
        const numberOps = [
            {value: ">", label: "é maior que"},
            {value: "<", label: "é menor que"},
            {value: ">=", label: "maior ou igual a"},
            {value: "<=", label: "menor ou igual a"},
            {value: "BETWEEN", label: "está entre"}
        ]
        const dateOps = [
            {value: ">", label: "é maior que"},
            {value: "<", label: "é menor que"},
            {value: "=", label: "é igual a"},
            {value: "TODAY", label: "é hoje"},
            {value: "THIS_MONTH", label: "neste mês"},
            {value: "LAST_30", label: "últimos 30 dias"},
            {value: "OVERDUE", label: "está vencido"},
            {value: "BETWEEN", label: "está entre"}
        ]

        if (fieldType === "text") return [...base, ...textOps]
        if (fieldType === "number") return [...base, ...numberOps]
        if (fieldType === "date") return [...base, ...dateOps]
        return base
    }

    onTableChange() {
        if (this.parsing) return
        this.rulesContainerTarget.innerHTML = ""
        this.fieldsContainerTarget.innerHTML = ""
        this.joinsContainerTarget.innerHTML = ""
        this.groupByContainerTarget.innerHTML = ""
        this.updateOrderFields()
        this.generateSql()
    }

    onJoinTableChange(event) {
        if (this.parsing) return
        const item = event.target.closest(".qb-item")
        this.autoFillJoinOn(item, event.target.value)
        this.updateOrderFields()
        this.generateSql()
    }

    onFieldChange(e) {
        const item = e.target.closest(".qb-item")
        const selectedOption = e.target.options[e.target.selectedIndex]
        const type = selectedOption ? selectedOption.dataset.type : "text"

        this.populateOperators(item, type)

        if (!this.parsing) this.generateSql()
    }

    onOperatorChange(event) {
        const item = event.target.closest(".qb-item")
        const operator = event.target.value
        const fieldType = item.dataset.fieldType || "text"

        this.updateInputLayout(item, fieldType, operator)

        if (!this.parsing) this.generateSql()
    }

    onTextInput() {
        this.generateSqlDebounced()
    }

    populateOperators(item, type) {
        const opSelect = item.querySelector(".qb-operator-select")
        item.dataset.fieldType = type
        opSelect.innerHTML = this.getOperators(type).map(op => `<option value="${op.value}">${op.label}</option>`).join("")
        opSelect.disabled = false
    }

    updateInputLayout(item, fieldType, operator) {
        const container = item.querySelector(".qb-value-wrap")
        const needsNoValue = ["IS NULL", "IS NOT NULL", "TODAY", "THIS_MONTH", "LAST_30", "OVERDUE"]

        if (needsNoValue.includes(operator)) {
            container.innerHTML = '<span class="text-muted small">Sem valor necessário</span>'
            return
        }

        if (operator === "BETWEEN") {
            const inputType = fieldType === "date" ? "date" : (fieldType === "number" ? "number" : "text")
            container.innerHTML = `
                <input type="${inputType}" class="form-control qb-value-1" placeholder="De..." data-action="input->query-builder#onTextInput">
                <span class="mx-1">até</span>
                <input type="${inputType}" class="form-control qb-value-2" placeholder="Até..." data-action="input->query-builder#onTextInput">`
            return
        }

        if (fieldType === "boolean") {
            container.innerHTML = `
                <select class="form-control" data-action="change->query-builder#generateSql">
                    <option value="true">Sim</option>
                    <option value="false">Não</option>
                </select>`
            return
        }

        const inputType = fieldType === "date" ? "date" : (fieldType === "number" ? "number" : "text")
        const placeholder = operator === "IN" ? "valor1, valor2, valor3" : "Digite o valor..."
        container.innerHTML = `<input type="${inputType}" class="form-control" placeholder="${placeholder}" data-action="input->query-builder#onTextInput">`
    }

    autoFillJoinOn(item, joinTableKey) {
        const onInput = item.querySelector(".qb-join-on")
        const mainTableKey = this.tableTarget.value

        if (joinTableKey && this.tables[joinTableKey] && mainTableKey && this.tables[mainTableKey]) {
            const mainTable = this.tables[mainTableKey]
            const joinTable = this.tables[joinTableKey]
            const fkPatterns = [
                `${mainTableKey.replace(/s$/, '')}_id`,
                `${mainTableKey}_id`,
                `socio_id`, `pessoa_id`
            ]

            let fkField = joinTable.fields.find(f => fkPatterns.includes(f.name))

            if (fkField) {
                onInput.value = `${mainTable.alias}.id = ${joinTable.alias}.${fkField.name}`
            } else {
                const reverseFk = mainTable.fields.find(f => f.name === `${joinTableKey.replace(/s$/, '')}_id`)
                if (reverseFk) {
                    onInput.value = `${mainTable.alias}.${reverseFk.name} = ${joinTable.alias}.id`
                } else {
                    onInput.value = ""
                    onInput.placeholder = `${mainTable.alias}.campo = ${joinTable.alias}.campo`
                }
            }
        }
    }

    updateOrderFields() {
        if (!this.hasOrderFieldTarget) return
        const allFields = this.getAllAvailableFields()
        const currentVal = this.orderFieldTarget.value

        let html = '<option value="">Nenhuma ordenação</option>'
        allFields.forEach(f => {
            html += `<option value="${f.fullName}">${f.label}</option>`
        })
        this.orderFieldTarget.innerHTML = html
        if (currentVal) this.orderFieldTarget.value = currentVal
    }

    getAllAvailableFields() {
        const fields = []
        const tableKey = this.tableTarget.value
        if (!tableKey || !this.tables[tableKey]) return fields

        const mainTable = this.tables[tableKey]
        mainTable.fields.forEach(f => {
            fields.push({
                fullName: `${mainTable.alias}.${f.name}`,
                label: f.label,
                type: f.type,
                indexed: f.indexed
            })
        })

        this.joinsContainerTarget.querySelectorAll(".qb-item").forEach(item => {
            const joinTableKey = item.querySelector(".qb-join-table")?.value
            if (joinTableKey && this.tables[joinTableKey]) {
                const joinTable = this.tables[joinTableKey]
                joinTable.fields.forEach(f => {
                    fields.push({
                        fullName: `${joinTable.alias}.${f.name}`,
                        label: `${joinTable.label} → ${f.label}`,
                        type: f.type,
                        indexed: f.indexed
                    })
                })
            }
        })
        return fields
    }

    getFieldOptionsHtml(includeAgg = false) {
        const fields = this.getAllAvailableFields()
        let html = '<option value="">Escolha um campo...</option>'
        if (includeAgg) html += '<option value="*">Todos os campos (*)</option>'

        const indexedFields = fields.filter(f => f.indexed)
        const regularFields = fields.filter(f => !f.indexed)

        if (indexedFields.length > 0) {
            html += '<optgroup label="⚡ Campos Indexados (Mais rápidos)">'
            indexedFields.forEach(f => {
                html += `<option value="${f.fullName}" data-type="${f.type}">⚡ ${f.label}</option>`
            })
            html += '</optgroup>'
        }

        if (regularFields.length > 0) {
            html += '<optgroup label="Outros Campos">'
            regularFields.forEach(f => {
                html += `<option value="${f.fullName}" data-type="${f.type}">${f.label}</option>`
            })
            html += '</optgroup>'
        }
        return html
    }

    addField() {
        const tableKey = this.tableTarget.value
        if (!tableKey && !this.parsing) {
            this.showToast("Primeiro escolha uma tabela principal", "warning")
            return
        }

        const id = `field-${++this.fieldIndex}`
        const funcOptions = this.getAggFunctions().map(f => `<option value="${f.value}">${f.label}</option>`).join("")

        const html = `
            <div class="qb-item" data-id="${id}">
                <select class="form-control qb-select-field" data-action="change->query-builder#generateSql">${this.getFieldOptionsHtml(true)}</select>
                <select class="form-control qb-select-func" data-action="change->query-builder#generateSql" title="Função (opcional)">${funcOptions}</select>
                <input type="text" class="form-control qb-select-alias" placeholder="Nome personalizado" data-action="input->query-builder#onTextInput" title="Renomear coluna (opcional)">
                <button type="button" class="btn btn-outline-danger btn-remove" data-action="click->query-builder#removeItem" title="Remover"><i class="fal fa-times"></i></button>
            </div>`

        this.fieldsContainerTarget.insertAdjacentHTML("beforeend", html)
        if (!this.parsing) this.generateSql()
    }

    addJoin() {
        const id = `join-${++this.joinIndex}`
        const tableOptions = Object.entries(this.tables)
            .filter(([key]) => key !== this.tableTarget.value)
            .map(([key, t]) => `<option value="${key}">${t.label}</option>`).join("")

        const html = `
            <div class="qb-item" data-id="${id}">
                <select class="form-control qb-join-type" data-action="change->query-builder#generateSql">${this.getJoinTypes().map(j => `<option value="${j.value}">${j.label}</option>`).join("")}</select>
                <select class="form-control qb-join-table" data-action="change->query-builder#onJoinTableChange"><option value="">Escolha...</option>${tableOptions}</select>
                <input type="text" class="form-control qb-join-on" placeholder="p.id = a.pessoa_id" data-action="input->query-builder#onTextInput">
                <button type="button" class="btn btn-outline-danger btn-remove" data-action="click->query-builder#removeJoin"><i class="fal fa-times"></i></button>
            </div>`
        this.joinsContainerTarget.insertAdjacentHTML("beforeend", html)
    }

    addRule() {
        const tableKey = this.tableTarget.value
        if (!tableKey && !this.parsing) {
            this.showToast("Primeiro escolha uma tabela principal", "warning")
            return
        }

        const id = `rule-${++this.ruleIndex}`
        const isFirst = this.rulesContainerTarget.children.length === 0
        let html = ""
        if (!isFirst) {
            html += `<div class="qb-connector"><select data-action="change->query-builder#generateSql"><option value="AND">E também</option><option value="OR">Ou então</option></select></div>`
        }

        html += `
            <div class="qb-item" data-id="${id}">
                <select class="form-control qb-field-select" data-action="change->query-builder#onFieldChange">${this.getFieldOptionsHtml()}</select>
                <select class="form-control qb-operator-select" data-action="change->query-builder#onOperatorChange"><option value="">Condição...</option></select>
                <div class="qb-value-wrap"><input type="text" class="form-control" placeholder="Valor..." data-action="input->query-builder#onTextInput"></div>
                <button type="button" class="btn btn-outline-danger btn-remove" data-action="click->query-builder#removeRule"><i class="fal fa-times"></i></button>
            </div>`

        this.rulesContainerTarget.insertAdjacentHTML("beforeend", html)

        const newItem = this.rulesContainerTarget.lastElementChild
        const fieldSelect = newItem.querySelector(".qb-field-select")

        if (fieldSelect.options.length > 1) {
            if (fieldSelect.value === "") {
                fieldSelect.selectedIndex = 1
            }

            const selectedOption = fieldSelect.options[fieldSelect.selectedIndex]
            const type = selectedOption.dataset.type || "text"

            this.populateOperators(newItem, type)

            const operatorSelect = newItem.querySelector(".qb-operator-select")
            if (operatorSelect.options.length > 0) {
                operatorSelect.selectedIndex = 0
                this.updateInputLayout(newItem, type, operatorSelect.value)
            }
        }

        if (!this.parsing) this.generateSql()
    }

    addGroupBy() {
        const tableKey = this.tableTarget.value
        if (!tableKey && !this.parsing) {
            this.showToast("Primeiro escolha uma tabela principal", "warning")
            return
        }
        const id = `groupby-${++this.groupByIndex}`
        const html = `
            <div class="qb-item" data-id="${id}">
                <select class="form-control" style="flex:1" data-action="change->query-builder#generateSql">${this.getFieldOptionsHtml()}</select>
                <button type="button" class="btn btn-outline-danger btn-remove" data-action="click->query-builder#removeItem" title="Remover"><i class="fal fa-times"></i></button>
            </div>`
        this.groupByContainerTarget.insertAdjacentHTML("beforeend", html)
        if (!this.parsing) this.generateSql()
    }

    removeJoin(event) {
        event.target.closest(".qb-item").remove()
        this.updateOrderFields()
        this.generateSql()
    }

    removeRule(event) {
        const item = event.target.closest(".qb-item")
        const connector = item.previousElementSibling
        if (connector?.classList.contains("qb-connector")) connector.remove()
        item.remove()
        const firstConnector = this.rulesContainerTarget.querySelector(".qb-connector")
        if (firstConnector && !firstConnector.previousElementSibling) firstConnector.remove()
        this.generateSql()
    }

    removeItem(event) {
        event.target.closest(".qb-item").remove()
        this.generateSql()
    }

    parseSqlToForm() {
        this.parsing = true
        const sql = this.sqlOutputTarget.value.trim()

        if (!sql) {
            this.parsing = false
            return
        }

        try {
            this.rulesContainerTarget.innerHTML = ""
            this.fieldsContainerTarget.innerHTML = ""
            this.joinsContainerTarget.innerHTML = ""
            this.groupByContainerTarget.innerHTML = ""

            const fromMatch = sql.match(/FROM\s+(\w+)\s+(\w+)/i)
            if (fromMatch) {
                const tableName = fromMatch[1]
                const tableKey = Object.keys(this.tables).find(k => this.tables[k].tableName === tableName)
                if (tableKey) this.tableTarget.value = tableKey
            }

            const joinMatches = Array.from(sql.matchAll(/(LEFT|INNER|RIGHT)\s+JOIN\s+(\w+)\s+(\w+)\s+ON\s+\((.*?)\)/gi))
            for (const match of joinMatches) {
                this.addJoin()
                const item = this.joinsContainerTarget.lastElementChild
                item.querySelector(".qb-join-type").value = match[1] + ' JOIN'
                item.querySelector(".qb-join-table").value = Object.keys(this.tables).find(k => this.tables[k].tableName === match[2]) || ""
                item.querySelector(".qb-join-on").value = match[4].trim()
            }

            this.updateOrderFields()

            const selectMatch = sql.match(/SELECT\s+(?:DISTINCT\s+)?(.*?)\s+FROM/is)
            if (selectMatch && selectMatch[1].trim() !== "*") {
                this.parseSelectFields(selectMatch[1].trim())
            }

            this.distinctTarget.value = sql.match(/SELECT\s+DISTINCT/i) ? "true" : "false"

            const limitMatch = sql.match(/LIMIT\s+(\d+)/i)
            if (limitMatch && this.hasLimitValueTarget) this.limitValueTarget.value = limitMatch[1]

            const orderMatch = sql.match(/ORDER BY\s+([\w.]+)\s+(ASC|DESC)?/i)
            if (orderMatch && this.hasOrderFieldTarget) {
                this.orderFieldTarget.value = orderMatch[1]
                if (orderMatch[2]) this.orderDirectionTarget.value = orderMatch[2].toUpperCase()
            }

            const whereMatch = sql.match(/WHERE\s+(.*?)(?:GROUP BY|ORDER BY|LIMIT|$)/is)
            if (whereMatch) {
                this.parseWhereClause(whereMatch[1].trim())
            }

            const groupMatch = sql.match(/GROUP BY\s+(.*?)(?:ORDER BY|LIMIT|$)/is)
            if (groupMatch) {
                groupMatch[1].split(',').forEach(g => {
                    this.addGroupBy()
                    const item = this.groupByContainerTarget.lastElementChild
                    item.querySelector('select').value = g.trim()
                })
            }

            this.showToast("Consulta carregada!", "success")
        } catch (error) {
            console.error("Erro ao processar SQL:", error)
            this.showToast("Erro ao ler SQL", "error")
        } finally {
            this.parsing = false
            this.generateSql()
        }
    }

    parseSelectFields(fieldsList) {
        const fields = []
        let current = ''
        let depth = 0

        for (let char of fieldsList) {
            if (char === '(') depth++
            if (char === ')') depth--
            if (char === ',' && depth === 0) {
                fields.push(current.trim())
                current = ''
            } else {
                current += char
            }
        }
        if (current) fields.push(current.trim())

        fields.forEach(fieldExpr => {
            this.addField()
            const item = this.fieldsContainerTarget.lastElementChild
            const match = fieldExpr.match(/^(?:(\w+)\((.*?)\)|([\w.*]+))(?:\s+AS\s+(\w+))?$/i)

            if (match) {
                const func = match[1] || ""
                const field = match[2] || match[3]
                const alias = match[4] || ""

                if(item.querySelector(".qb-select-field")) item.querySelector(".qb-select-field").value = field
                if(item.querySelector(".qb-select-func")) item.querySelector(".qb-select-func").value = func
                if(item.querySelector(".qb-select-alias")) item.querySelector(".qb-select-alias").value = alias
            }
        })
    }

    parseWhereClause(whereClause) {
        whereClause = whereClause.replace(/\s+/g, ' ').trim()
        const conditions = []
        let connector = "AND"
        const tokens = whereClause.split(/\s+(AND|OR)\s+/i)

        for (let i = 0; i < tokens.length; i++) {
            if (tokens[i].match(/^(AND|OR)$/i)) {
                connector = tokens[i].toUpperCase()
            } else {
                conditions.push({connector, condition: tokens[i].trim()})
                connector = "AND"
            }
        }

        conditions.forEach((cond, index) => {
            this.addRule()
            const item = this.rulesContainerTarget.lastElementChild

            if (index > 0) {
                const connectorEl = item.previousElementSibling
                if (connectorEl?.classList.contains("qb-connector")) {
                    connectorEl.querySelector("select").value = cond.connector
                }
            }
            this.parseCondition(cond.condition, item)
        })
    }

    parseCondition(condition, item) {
        const patterns = [
            {regex: /^([\w.]+)\s+IS\s+NULL$/i, operator: "IS NULL"},
            {regex: /^([\w.]+)\s+IS\s+NOT\s+NULL$/i, operator: "IS NOT NULL"},
            {regex: /^([\w.]+)\s+LIKE\s+'%(.+)%'$/i, operator: "LIKE"},
            {regex: /^([\w.]+)\s+NOT\s+LIKE\s+'%(.+)%'$/i, operator: "NOT LIKE"},
            {regex: /^([\w.]+)\s+LIKE\s+'(.+)%'$/i, operator: "STARTS"},
            {regex: /^([\w.]+)\s+LIKE\s+'%(.+)'$/i, operator: "ENDS"},
            {regex: /^([\w.]+)\s+BETWEEN\s+(.+)\s+AND\s+(.+)$/i, operator: "BETWEEN"},
            {regex: /^([\w.]+)\s+IN\s+\((.+)\)$/i, operator: "IN"},
            {regex: /^([\w.]+)\s*(=|!=|>|<|>=|<=)\s*(.+)$/i, operator: null}
        ]

        for (const pattern of patterns) {
            const match = condition.match(pattern.regex)
            if (match) {
                const field = match[1]
                const operator = pattern.operator || match[2]
                const fieldSelect = item.querySelector(".qb-field-select")
                fieldSelect.value = field

                const selectedOption = fieldSelect.options[fieldSelect.selectedIndex]
                const type = selectedOption?.dataset?.type || "text"

                this.populateOperators(item, type)

                const operatorSelect = item.querySelector(".qb-operator-select")
                operatorSelect.value = operator

                this.updateInputLayout(item, type, operator)

                if (operator === "BETWEEN") {
                    const val1 = item.querySelector(".qb-value-1")
                    const val2 = item.querySelector(".qb-value-2")
                    if (val1) val1.value = this.unescapeValue(match[2])
                    if (val2) val2.value = this.unescapeValue(match[3])
                } else if (operator === "IN") {
                    const valueInput = item.querySelector(".qb-value-wrap input")
                    if (valueInput) {
                        valueInput.value = match[2].split(',')
                            .map(v => this.unescapeValue(v.trim()))
                            .join(', ')
                    }
                } else if (match[2] || match[3]) {
                    const valueInput = item.querySelector(".qb-value-wrap input, .qb-value-wrap select")
                    if (valueInput) {
                        valueInput.value = this.unescapeValue(match[3] || match[2])
                    }
                }

                break
            }
        }
    }

    generateSql() {
        if (this.parsing || !this.tableTarget.value) return
        const mainTable = this.tables[this.tableTarget.value]
        if (!mainTable) return

        let sql = `SELECT ${this.distinctTarget.value === "true" ? "DISTINCT " : ""}`

        const fields = this.buildSelectFields()
        sql += fields.length === 0 ? "*" : fields.join(", ")
        sql += `\nFROM ${mainTable.tableName} ${mainTable.alias}`

        sql += this.buildJoins()
        const where = this.buildWhere()
        if (where) sql += `\nWHERE ${where}`

        const groupBy = this.buildGroupBy()
        if (groupBy) sql += `\nGROUP BY ${groupBy}`

        if (this.hasOrderFieldTarget && this.orderFieldTarget.value) {
            sql += `\nORDER BY ${this.orderFieldTarget.value} ${this.orderDirectionTarget.value}`
        }

        if (this.limitValueTarget.value) sql += `\nLIMIT ${this.limitValueTarget.value}`

        this.sqlOutputTarget.value = sql
        this.analyzePerformance(mainTable, fields.length === 0)
    }

    buildSelectFields() {
        const fields = []
        this.fieldsContainerTarget.querySelectorAll(".qb-item").forEach(item => {
            const field = item.querySelector(".qb-select-field").value
            const func = item.querySelector(".qb-select-func").value
            const alias = item.querySelector(".qb-select-alias").value.trim()
            if (field) {
                let expr = func ? `${func}(${field === '*' && func.startsWith('COUNT') ? '*' : field})` : field
                if (alias) expr += ` AS ${alias}`
                fields.push(expr)
            }
        })
        return fields
    }

    buildJoins() {
        let sql = ""
        if (!this.hasJoinsContainerTarget) return sql
        this.joinsContainerTarget.querySelectorAll(".qb-item").forEach(item => {
            const joinType = item.querySelector(".qb-join-type")?.value
            const joinTableKey = item.querySelector(".qb-join-table")?.value
            const onClause = item.querySelector(".qb-join-on")?.value?.trim()
            if (!joinType || !joinTableKey || !onClause || !this.tables[joinTableKey]) return
            const joinTable = this.tables[joinTableKey]
            sql += `\n${joinType} ${joinTable.tableName} ${joinTable.alias} ON (${onClause})`
        })
        return sql
    }

    buildWhere() {
        const conditions = []
        if (!this.hasRulesContainerTarget) return ""
        this.rulesContainerTarget.querySelectorAll(".qb-item").forEach((item, idx) => {
            const field = item.querySelector(".qb-field-select")?.value
            const operator = item.querySelector(".qb-operator-select")?.value
            const valueInput = item.querySelector(".qb-value-wrap input, .qb-value-wrap select")
            const valueInput2 = item.querySelector(".qb-value-wrap .qb-value-2")
            const connector = item.previousElementSibling?.classList.contains("qb-connector")
                ? item.previousElementSibling.querySelector("select")?.value
                : "AND"

            if (!field || !operator) return
            let cond = ""
            const fieldType = item.dataset.fieldType || "text"

            if (operator === "IS NULL") cond = `${field} IS NULL`
            else if (operator === "IS NOT NULL") cond = `${field} IS NOT NULL`
            else if (operator === "TODAY") cond = `${field} = CURRENT_DATE`
            else if (operator === "THIS_MONTH") cond = `EXTRACT(MONTH FROM ${field}) = EXTRACT(MONTH FROM CURRENT_DATE) AND EXTRACT(YEAR FROM ${field}) = EXTRACT(YEAR FROM CURRENT_DATE)`
            else if (operator === "LAST_30") cond = `${field} >= CURRENT_DATE - INTERVAL '30 days'`
            else if (operator === "OVERDUE") cond = `${field} < CURRENT_DATE`
            else if (operator === "LIKE" && valueInput?.value) cond = `${field} LIKE '%${this.escape(valueInput.value)}%'`
            else if (operator === "NOT LIKE" && valueInput?.value) cond = `${field} NOT LIKE '%${this.escape(valueInput.value)}%'`
            else if (operator === "STARTS" && valueInput?.value) cond = `${field} LIKE '${this.escape(valueInput.value)}%'`
            else if (operator === "ENDS" && valueInput?.value) cond = `${field} LIKE '%${this.escape(valueInput.value)}'`
            else if (operator === "BETWEEN" && valueInput?.value && valueInput2?.value) cond = `${field} BETWEEN ${this.formatValue(valueInput.value, fieldType)} AND ${this.formatValue(valueInput2.value, fieldType)}`
            else if (operator === "IN" && valueInput?.value) {
                const values = valueInput.value.split(",").map(v => this.formatValue(v.trim(), fieldType)).join(", ")
                cond = `${field} IN (${values})`
            } else if (valueInput?.value !== undefined && valueInput?.value !== "") {
                cond = `${field} ${operator} ${this.formatValue(valueInput.value, fieldType)}`
            }
            if (cond) conditions.push({connector: idx === 0 ? "" : connector, cond})
        })
        return conditions.map((c, i) => i === 0 ? c.cond : `${c.connector} ${c.cond}`).join("\n  ")
    }

    buildGroupBy() {
        const fields = []
        if (!this.hasGroupByContainerTarget) return ""
        this.groupByContainerTarget.querySelectorAll(".qb-item").forEach(item => {
            const field = item.querySelector("select")?.value
            if (field) fields.push(field)
        })
        return fields.join(", ")
    }

    formatValue(val, type) {
        if (type === "number") return isNaN(val) ? `'${this.escape(val)}'` : val
        if (type === "boolean") return val === "true" ? "TRUE" : "FALSE"
        return `'${this.escape(val)}'`
    }

    escape(str) {
        return String(str).replace(/'/g, "''")
    }

    unescapeValue(val) {
        return val.replace(/^'|'$/g, '').replace(/''/g, "'")
    }

    copySql() {
        const sql = this.hasSqlOutputTarget ? this.sqlOutputTarget.value : ""
        if (sql && navigator.clipboard) {
            navigator.clipboard.writeText(sql)
            this.showToast("Consulta copiada!", "success")
        }
    }

    loadTemplate(event) {
        const template = event.target.value
        if (!template) return
        const templates = {
            adesoes_ativas: {
                table: 'adesoes',
                rules: [{field: 'a.status', operator: '=', value: 'ativa'}],
                order: 'a.created_at', direction: 'DESC'
            },
            aniversariantes: {
                table: 'pessoas',
                rules: [{field: 'p.data_nascimento', operator: 'THIS_MONTH', value: null}],
                order: 'p.data_nascimento', direction: 'ASC'
            }
        }
        const t = templates[template]
        if (t) {
            this.tableTarget.value = t.table
            this.updateOrderFields()
            this.rulesContainerTarget.innerHTML = ""
            t.rules.forEach(rule => {
                this.addRule()
                const item = this.rulesContainerTarget.lastElementChild
                const fieldSelect = item.querySelector(".qb-field-select")
                fieldSelect.value = rule.field

                const selectedOption = Array.from(fieldSelect.options).find(opt => opt.value === rule.field)
                const type = selectedOption ? selectedOption.dataset.type : "text"
                this.populateOperators(item, type)

                const opSelect = item.querySelector(".qb-operator-select")
                opSelect.value = rule.operator
                this.updateInputLayout(item, type, rule.operator)

                if (rule.value) {
                    const valueInput = item.querySelector(".qb-value-wrap input")
                    if (valueInput) valueInput.value = rule.value
                }
            })
            if (t.order) this.orderFieldTarget.value = t.order
            if (t.direction) this.orderDirectionTarget.value = t.direction
            this.generateSql()
        }
    }

    showToast(msg, type = "info") {
        if (window.toastr) window.toastr[type](msg)
        else alert(msg)
    }

    analyzePerformance(mainTable, useSelectAll) {
        const hints = []
        if (useSelectAll) hints.push({type: 'warning', message: 'Dica: Selecione apenas os campos necessários.'})

        if (this.hasOrderFieldTarget && this.orderFieldTarget.value) {
            const orderField = this.orderFieldTarget.value.split('.').pop()
            const field = mainTable.fields?.find(f => f.name === orderField)
            if (field && !field.indexed) hints.push({type: 'warning', message: `O campo de ordenação "${field.label}" não possui índice.`})
        }

        this.displayPerformanceHints(hints)
    }

    displayPerformanceHints(hints) {
        let container = document.getElementById('performance-hints-container')
        if (!container) {
            container = document.createElement('div')
            container.id = 'performance-hints-container'
            container.className = 'mt-3'
            if (this.hasSqlOutputTarget) this.sqlOutputTarget.parentElement.appendChild(container)
        }
        if (hints.length === 0) {
            container.innerHTML = ''
            return
        }
        container.innerHTML = `
            <div class="alert alert-light border p-3">
                <h6 class="mb-2"><i class="fal fa-tachometer-alt mr-1"></i> Análise de Performance</h6>
                ${hints.map(hint => `<div class="text-${hint.type === 'warning' ? 'warning' : 'info'} small mb-1"><i class="fal fa-info-circle mr-1"></i>${hint.message}</div>`).join('')}
            </div>`
    }
}